/**
 * SIL-1565 - the ONE place a buyer-listing price is derived from the
 * tier-tagged `pricing` union. Components render the returned view and branch
 * only on `view.mode` where the LAYOUT genuinely differs (buttons, strip
 * content); they never re-derive list price, total, FMV band or range mode.
 *
 * ADR 0013 buyer-safe posture lives here once: the 'ask' view carries no
 * range, no FMV band, no market label and no pct-vs-FMV, and the 'unverified'
 * view carries no exact price. The view is built from the DTO the server
 * already sent - nothing here can reveal a figure the DTO did not carry.
 *
 * Pure: no React, no I/O.
 */
import type { FmvBandDto, MarketLabel, PriceRange } from '../../dto/buyer-tier.js';
import { formatPriceRange } from './formatPriceRange.js';
import { nodeCountFromEvidence, type BuyerEvidence, type BuyerPricing } from './listingShape.js';

/** SIL-1578: shown where a per-GPU figure is unknown (mixed lot, no GPU count). */
export const PER_GPU_UNKNOWN = 'n/a';

const usd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

/** `totalUsd / unitCount`, clamped so a 0 unit count cannot produce Infinity. */
export function pricePerUnitUsd(totalUsd: number, unitCount: number): number {
  return totalUsd / Math.max(1, unitCount);
}

/** `pricePerUnitUsd` applied to a `{ lo, hi }` range. */
export function pricePerUnitRange(total: PriceRange, unitCount: number): PriceRange {
  return { lo: pricePerUnitUsd(total.lo, unitCount), hi: pricePerUnitUsd(total.hi, unitCount) };
}

export interface ListingPriceView {
  /** The server's tier decision, for layout branching only. */
  mode: 'verified' | 'unverified' | 'ask';
  /** Per-GPU headline: exact dollars, or a bucketed range for 'unverified'. */
  headline: string;
  /** Per-GPU figure (ask mode repeats its single flat price). */
  perGpu: string;
  perNode: string;
  /** Lot total. */
  total: string;
  /** Total divided by the unit count (listing card "Price / Unit"). */
  perUnit: string;
  /** Exact list price for offer maths. null for 'unverified' (never known). */
  listPriceUsd: number | null;
  /** FMV median for offer maths. Verified only. */
  fmvMedian: number | null;
  /** Bucketed total range, 'unverified' only; basis of the offer prefill. */
  totalRange: PriceRange | null;
  /** FMV band, verified only. */
  fmvBand: FmvBandDto | null;
  pctVsFmv: number | null;
  marketLabel: MarketLabel | null;
}

export interface ListingPriceViewOptions {
  /**
   * Units the total is spread over. Defaults to the evidence node count,
   * floored at 1. Callers with a better count (the card's `unitCountOf`)
   * pass it.
   */
  unitCount?: number;
}

export function listingPriceView(
  pricing: BuyerPricing,
  evidence?: BuyerEvidence,
  options: ListingPriceViewOptions = {},
): ListingPriceView {
  const unitCount = Math.max(1, options.unitCount ?? nodeCountFromEvidence(evidence));

  if (pricing.tier === 'ask') {
    const price = usd.format(pricing.priceUsd);
    return {
      mode: 'ask',
      headline: price,
      perGpu: price,
      perNode: price,
      total: usd.format(pricing.priceUsd * unitCount),
      perUnit: price,
      listPriceUsd: pricing.priceUsd,
      fmvMedian: null,
      totalRange: null,
      fmvBand: null,
      pctVsFmv: null,
      marketLabel: null,
    };
  }

  if (pricing.tier === 'verified') {
    const perGpu = pricing.pricePerGpuUsd === null ? PER_GPU_UNKNOWN : usd.format(pricing.pricePerGpuUsd);
    return {
      mode: 'verified',
      headline: perGpu,
      perGpu,
      perNode: usd.format(pricing.pricePerNodeUsd),
      total: usd.format(pricing.totalUsd),
      perUnit: usd.format(pricePerUnitUsd(pricing.totalUsd, unitCount)),
      listPriceUsd: pricing.totalUsd,
      fmvMedian: pricing.fmvBand?.median ?? null,
      totalRange: null,
      fmvBand: pricing.fmvBand,
      pctVsFmv: pricing.pctVsFmv,
      marketLabel: null,
    };
  }

  const perGpu =
    pricing.priceRange.perGpu === null ? PER_GPU_UNKNOWN : formatPriceRange(pricing.priceRange.perGpu);
  return {
    mode: 'unverified',
    headline: perGpu,
    perGpu,
    perNode: formatPriceRange(pricing.priceRange.perNode),
    total: formatPriceRange(pricing.priceRange.total),
    perUnit: formatPriceRange(pricePerUnitRange(pricing.priceRange.total, unitCount)),
    listPriceUsd: null,
    fmvMedian: null,
    totalRange: pricing.priceRange.total,
    fmvBand: null,
    pctVsFmv: null,
    marketLabel: pricing.marketLabel,
  };
}
